import { useNavigate } from 'react-router-dom'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Settings } from '@factorialco/f0-react/icons/app'
import {
  PeopleDataCollectionDocument,
  PeopleDataCollectionQuery,
  PerformanceCyclesCycleFilterDocument,
} from 'generated/resources/graphql'
import { sortBy } from 'lodash'

import buildUrl from 'modules/core/lib/buildUrl'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'

import { NO_CYCLE_OPTION } from 'modules/performance_cycles/components/CycleFilter'

type PeopleDataCollectionNode = NonNullable<
  PeopleDataCollectionQuery['goals']['employeesConnection']
>['edges'][number]['node']

type CycleFilterNode = {
  id: string
  title: string
  [key: string]: unknown
}

type PeopleFiltersStorage = {
  cycle?: string[]
  onlyDirectReports?: string[]
  onlyWithGoals?: string[]
}

const DEFAULT_FILTERS: PeopleFiltersStorage = {
  onlyDirectReports: ['true'],
  onlyWithGoals: ['true'],
}

function toStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return v.filter((x): x is string => typeof x === 'string')
}

type EmployeeStatus = {
  label: string
  status: 'positive' | 'neutral' | 'negative' | 'warning'
}

function getEmployeeStatus(node: PeopleDataCollectionNode): EmployeeStatus {
  const cycleProgress = node.progresses[0]
  const progress = cycleProgress?.progress ?? node.metricSummary?.averageProgress ?? 0
  const needsAttention =
    cycleProgress?.needsAttention ?? node.metricSummary?.needsAttention ?? false
  const goalsCount = node.metricSummary?.metricsCount ?? 0

  if (goalsCount === 0) {
    return { label: i18n.t('goals_revamp.people_list.status.no_goals'), status: 'neutral' }
  }
  if (progress >= 100) {
    return { label: i18n.t('goals_revamp.people_list.status.completed'), status: 'positive' }
  }
  if (needsAttention) {
    return { label: i18n.t('goals_revamp.people_list.status.at_risk'), status: 'warning' }
  }
  if (progress > 0) {
    return { label: i18n.t('goals_revamp.people_list.status.in_progress'), status: 'neutral' }
  }
  return { label: i18n.t('goals_revamp.people_list.status.not_started'), status: 'neutral' }
}

interface PeopleDataCollectionProps {
  canManageCycles: boolean
  onManageCycles: () => void
  canCreateGoal: boolean
  onCreate: () => void
}

const TABLE_COLUMNS = [
  {
    key: 'name',
    label: () => i18n.t('goals_revamp.people_list.columns.name'),
    render: (item: PeopleDataCollectionNode) => {
      const avatar = item.employee?.access?.avatar
      return {
        type: 'person' as const,
        value: {
          firstName: item.employee?.firstName ?? '',
          lastName: item.employee?.lastName ?? '',
          src: avatar ? buildUrl(avatar) : undefined,
        },
      }
    },
  },
  {
    key: 'manager',
    label: () => i18n.t('goals_revamp.people_list.columns.manager'),
    render: (item: PeopleDataCollectionNode) => {
      const managerName = item.employee?.manager?.fullName
      return { type: 'text' as const, value: managerName ?? { placeholder: '-' } }
    },
  },
  {
    key: 'goals_count',
    label: () => i18n.t('goals_revamp.people_list.columns.goals_count'),
    align: 'right' as const,
    render: (item: PeopleDataCollectionNode) => {
      const count = item.metricSummary?.metricsCount ?? 0
      return { type: 'text' as const, value: count > 0 ? count.toString() : { placeholder: '-' } }
    },
  },
  {
    key: 'progress',
    label: () => i18n.t('goals_revamp.people_list.columns.progress'),
    align: 'right' as const,
    render: (item: PeopleDataCollectionNode) => {
      const cycleProgress = item.progresses[0]
      const progress = Math.min(
        cycleProgress ? cycleProgress.progress : (item.metricSummary?.averageProgress ?? 0),
        100
      )
      return {
        type: 'progressBar' as const,
        value: {
          value: progress,
          max: 100,
          label: `${i18n.formatNumber(progress)}%`,
        },
      }
    },
  },
  {
    key: 'status',
    label: () => i18n.t('goals_revamp.people_list.columns.status'),
    render: (item: PeopleDataCollectionNode) => {
      const status = getEmployeeStatus(item)
      return { type: 'status' as const, value: { label: status.label, status: status.status } }
    },
  },
]

export const PeopleDataCollection: React.FC<PeopleDataCollectionProps> = ({
  canManageCycles,
  onManageCycles,
  canCreateGoal,
  onCreate,
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const [savedFilters, setSavedFilters] = useLocalStorage<PeopleFiltersStorage>(
    LOCAL_STORAGE_KEYS.goalsPeopleFilters,
    DEFAULT_FILTERS
  )

  const cycleFilterSource = useGraphqlDataSource(
    {
      document: PerformanceCyclesCycleFilterDocument,
      fetchPolicy: 'cache-first',
      perPage: 200,
      mapData: (data): CycleFilterNode[] => {
        const sortedCycles = sortBy(data.performanceCycles.cycles ?? [], ['title'])

        return [
          {
            id: NO_CYCLE_OPTION,
            title: i18n.t('performance_cycles.cycle_filter.no_cycle_option'),
          },
          ...sortedCycles.map((cycle) => ({ ...cycle, title: cycle.title ?? '' })),
        ]
      },
      mapPagination: (data) => ({
        total: (data.performanceCycles.cycles?.length ?? 0) + 1,
      }),
      mapVariables: () => ({}),
    },
    []
  )

  const source = useGraphqlDataCollectionSource<PeopleDataCollectionNode>({
    document: PeopleDataCollectionDocument,
    mapData: (data) => {
      const edges: PeopleDataCollectionQuery['goals']['employeesConnection']['edges'] =
        data.goals.employeesConnection?.edges ?? []
      return edges.map((edge) => edge.node).filter(Boolean)
    },
    mapVariables: ({ search: searchTerm, filters }) => {
      const cycleValues = toStringArray(filters?.cycle)
      const hasNoCycle = cycleValues.includes(NO_CYCLE_OPTION)
      const cycleIds = cycleValues.filter((v) => v !== NO_CYCLE_OPTION)

      const onlyDirectReportsValues = toStringArray(filters?.onlyDirectReports)
      const onlyDirectReports =
        onlyDirectReportsValues.length === 1 ? onlyDirectReportsValues[0] === 'true' : undefined

      const onlyWithGoalsValues = toStringArray(filters?.onlyWithGoals)
      const onlyWithGoals =
        onlyWithGoalsValues.length === 1 ? onlyWithGoalsValues[0] === 'true' : undefined

      setSavedFilters({
        cycle: cycleValues.length ? cycleValues : undefined,
        onlyDirectReports: onlyDirectReportsValues.length ? onlyDirectReportsValues : undefined,
        onlyWithGoals: onlyWithGoalsValues.length ? onlyWithGoalsValues : undefined,
      })

      return {
        cycleIds: cycleIds.length ? cycleIds : undefined,
        noCycle: hasNoCycle,
        onlyDirectReports,
        onlyWithGoals,
        search: searchTerm ?? undefined,
      }
    },
    mapPagination: (data) => {
      const conn = data.goals.employeesConnection
      const pageInfo = conn?.pageInfo
      const totalCount = conn?.totalCount ?? 0
      const edges = conn?.edges ?? []
      const lastEdge = edges[edges.length - 1]

      return {
        total: totalCount,
        cursor: lastEdge?.cursor ?? pageInfo?.endCursor ?? undefined,
        hasNextPage: pageInfo?.hasNextPage ?? false,
      }
    },
    paginationType: 'infinite-scroll',
    perPage: 25,
    totalItemSummary: (totals) =>
      i18n.tp('goals_revamp.people_list.total_items', { count: totals }),
    itemOnClick: (item) => () => {
      navigate(
        navigation.goals.employees.employee.fullPath({
          id: item.id.toString(),
        })
      )
    },
    primaryActions: () =>
      canCreateGoal
        ? {
            label: i18n.t('goals_revamp.people_list.create_button'),
            icon: Add,
            onClick: onCreate,
          }
        : undefined,
    secondaryActions: () =>
      canManageCycles
        ? [
            {
              label: i18n.t('performance_goals.cycles.manage_cycles'),
              icon: Settings,
              onClick: onManageCycles,
            },
          ]
        : undefined,
    search: {
      enabled: true,
    },
    defaultFilters: {
      onlyDirectReports: ['true'],
      onlyWithGoals: ['true'],
    },
    currentFilters: savedFilters ?? DEFAULT_FILTERS,
    filters: {
      cycle: {
        type: 'in',
        label: i18n.t('performance_cycles.cycle_filter.label'),
        options: {
          source: cycleFilterSource,
          mapOptions: (cycle: CycleFilterNode) => ({
            label: cycle.title,
            value: cycle.id,
          }),
          cache: true,
        },
      },
      onlyDirectReports: {
        type: 'in',
        label: i18n.t('performance_goals.employee_list.only_direct_reports'),
        options: {
          options: async () => [
            {
              label: i18n.t('goals_revamp.people_list.filter_only_direct_reports.yes'),
              value: 'true',
            },
            {
              label: i18n.t('goals_revamp.people_list.filter_only_direct_reports.no'),
              value: 'false',
            },
          ],
          cache: true,
        },
      },
      onlyWithGoals: {
        type: 'in',
        label: i18n.t('goals_revamp.people_list.filter_only_with_goals.label'),
        options: {
          options: async () => [
            { label: i18n.t('goals_revamp.people_list.filter_only_with_goals.yes'), value: 'true' },
            { label: i18n.t('goals_revamp.people_list.filter_only_with_goals.no'), value: 'false' },
          ],
          cache: true,
        },
      },
    },
  })

  return (
    <OneDataCollection
      source={source}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: TABLE_COLUMNS.map((col) => ({
              ...(col.align && { align: col.align }),
              label: col.label(),
              render: col.render,
            })),
          },
        },
      ]}
    />
  )
}
